/**
 * AI Results Modal
 *
 * Displays AI analysis results and allows the manager to selectively
 * apply suggestions to the wizard state.
 */

import { useState } from 'react';
import { Check, Info, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import type { AIAnalysisResult } from '@functieloonhuis/shared';
import { useWizard } from '../WizardContext';
import type { TaskScore, CompetencyScore } from '../../../../lib/evaluationsApi';

interface AIResultsModalProps {
  open: boolean;
  onClose: () => void;
  result: AIAnalysisResult;
  evaluationId: number;
  onApplied: () => void;
}

export function AIResultsModal({
  open,
  onClose,
  result,
  onApplied,
}: AIResultsModalProps) {
  const { stepData, updateStepData } = useWizard();

  // Track which suggestions are selected
  const [selectedTasks, setSelectedTasks] = useState<Set<number>>(
    new Set(result.taskSuggestions.filter((t) => t.suggestedScore !== null).map((t) => t.taskId))
  );
  const [selectedVakComps, setSelectedVakComps] = useState<Set<number>>(
    new Set(
      result.vakCompetencySuggestions
        .filter((c) => c.suggestedScore !== null)
        .map((c) => c.competencyId)
    )
  );
  const [selectedBasisComps, setSelectedBasisComps] = useState<Set<number>>(
    new Set(
      result.basisCompetencySuggestions
        .filter((c) => c.suggestedScore !== null)
        .map((c) => c.competencyId)
    )
  );

  // Helper: merge toelichtingen
  const mergeToelichting = (existing: string | undefined | null, suggested: string): string => {
    const existingStr = existing ?? '';
    if (!existingStr.trim()) return suggested;
    if (!suggested?.trim()) return existingStr;
    // If both exist, combine
    if (existingStr.includes(suggested)) return existingStr;
    return `${existingStr}\n\n${suggested}`;
  };

  const handleApply = () => {
    // Merge AI suggestions with existing data
    // The wizard stores TaskScore[] and CompetencyScore[] with score: number (non-null)
    // We need to merge selected AI suggestions with existing scores

    // 1. Task Scores (Step 2)
    const existingTaskScores = stepData.step2?.taskScores || [];
    const existingTaskAgreements = stepData.step2?.taskAgreements || {};

    // Create a map of existing scores for quick lookup
    const taskScoreMap = new Map<number, TaskScore>();
    existingTaskScores.forEach((s) => taskScoreMap.set(s.taskId, s));

    // Apply selected AI suggestions
    result.taskSuggestions.forEach((suggestion) => {
      if (!selectedTasks.has(suggestion.taskId) || suggestion.suggestedScore === null) return;

      const existing = taskScoreMap.get(suggestion.taskId);
      const newScore: TaskScore = {
        taskId: suggestion.taskId,
        score: suggestion.suggestedScore,
        toelichting: mergeToelichting(existing?.toelichting, suggestion.toelichting) || undefined,
        selectedAgreementTypes:
          suggestion.requiresAgreement && suggestion.suggestedAgreement
            ? [suggestion.suggestedAgreement.typeId]
            : existing?.selectedAgreementTypes,
      };
      taskScoreMap.set(suggestion.taskId, newScore);

      // Handle agreements
      if (suggestion.requiresAgreement && suggestion.suggestedAgreement) {
        existingTaskAgreements[suggestion.taskId] = [
          {
            agreementTypeId: suggestion.suggestedAgreement.typeId,
            beschrijving: suggestion.suggestedAgreement.beschrijving,
          },
        ];
      }
    });

    const newTaskScores = Array.from(taskScoreMap.values());

    // 2. Vak Competency Scores (Step 3)
    const existingVakScores = stepData.step3?.vakCompetencyScores || [];
    const existingVakAgreements = stepData.step3?.vakCompetencyAgreements || {};

    const vakScoreMap = new Map<number, CompetencyScore>();
    existingVakScores.forEach((s) => vakScoreMap.set(s.competencyId, s));

    result.vakCompetencySuggestions.forEach((suggestion) => {
      if (!selectedVakComps.has(suggestion.competencyId) || suggestion.suggestedScore === null) return;

      const existing = vakScoreMap.get(suggestion.competencyId);
      const newScore: CompetencyScore = {
        competencyId: suggestion.competencyId,
        score: suggestion.suggestedScore,
        toelichting: mergeToelichting(existing?.toelichting, suggestion.toelichting) || undefined,
        selectedAgreementTypes:
          suggestion.requiresAgreement && suggestion.suggestedAgreement
            ? [suggestion.suggestedAgreement.typeId]
            : existing?.selectedAgreementTypes,
      };
      vakScoreMap.set(suggestion.competencyId, newScore);

      if (suggestion.requiresAgreement && suggestion.suggestedAgreement) {
        existingVakAgreements[suggestion.competencyId] = [
          {
            agreementTypeId: suggestion.suggestedAgreement.typeId,
            beschrijving: suggestion.suggestedAgreement.beschrijving,
          },
        ];
      }
    });

    const newVakScores = Array.from(vakScoreMap.values());

    // 3. Basis Competency Scores (Step 4)
    const existingBasisScores = stepData.step4?.basisCompetencyScores || [];
    const existingBasisAgreements = stepData.step4?.basisCompetencyAgreements || {};

    const basisScoreMap = new Map<number, CompetencyScore>();
    existingBasisScores.forEach((s) => basisScoreMap.set(s.competencyId, s));

    result.basisCompetencySuggestions.forEach((suggestion) => {
      if (!selectedBasisComps.has(suggestion.competencyId) || suggestion.suggestedScore === null) return;

      const existing = basisScoreMap.get(suggestion.competencyId);
      const newScore: CompetencyScore = {
        competencyId: suggestion.competencyId,
        score: suggestion.suggestedScore,
        toelichting: mergeToelichting(existing?.toelichting, suggestion.toelichting) || undefined,
        selectedAgreementTypes:
          suggestion.requiresAgreement && suggestion.suggestedAgreement
            ? [suggestion.suggestedAgreement.typeId]
            : existing?.selectedAgreementTypes,
      };
      basisScoreMap.set(suggestion.competencyId, newScore);

      if (suggestion.requiresAgreement && suggestion.suggestedAgreement) {
        existingBasisAgreements[suggestion.competencyId] = [
          {
            agreementTypeId: suggestion.suggestedAgreement.typeId,
            beschrijving: suggestion.suggestedAgreement.beschrijving,
          },
        ];
      }
    });

    const newBasisScores = Array.from(basisScoreMap.values());

    // Update wizard state
    updateStepData('step2', {
      ...stepData.step2,
      taskScores: newTaskScores,
      taskAgreements: existingTaskAgreements,
    });
    updateStepData('step3', {
      ...stepData.step3,
      vakCompetencyScores: newVakScores,
      vakCompetencyAgreements: existingVakAgreements,
    });
    updateStepData('step4', {
      ...stepData.step4,
      basisCompetencyScores: newBasisScores,
      basisCompetencyAgreements: existingBasisAgreements,
    });

    const appliedCount = selectedTasks.size + selectedVakComps.size + selectedBasisComps.size;
    toast.success(`${appliedCount} suggesties toegepast`);
    onApplied();
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.8)
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          Hoog
        </span>
      );
    if (confidence >= 0.6)
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
          Medium
        </span>
      );
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
        Laag
      </span>
    );
  };

  const getScoreColor = (score: number | null) => {
    if (!score) return 'text-gray-400';
    if (score <= 2) return 'text-red-600';
    if (score === 3) return 'text-yellow-600';
    return 'text-green-600';
  };

  const totalSelected = selectedTasks.size + selectedVakComps.size + selectedBasisComps.size;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-medium text-gray-900">AI Analyse Resultaten</h3>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
            <p className="text-sm text-blue-800">
              <Info className="inline h-4 w-4 mr-1" />
              <strong>Samenvatting:</strong> {result.summary}
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[500px] overflow-y-auto">
            {/* Taken */}
            <section className="mb-6">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                Taken
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                  {selectedTasks.size}/{result.taskSuggestions.length}
                </span>
              </h4>

              <div className="space-y-3">
                {result.taskSuggestions.map((task) => (
                  <SuggestionCard
                    key={task.taskId}
                    name={task.taskName}
                    score={task.suggestedScore}
                    confidence={task.confidence}
                    toelichting={task.toelichting}
                    reasoning={task.reasoning}
                    sourceQuotes={task.sourceQuotes}
                    requiresAgreement={task.requiresAgreement}
                    agreement={task.suggestedAgreement}
                    selected={selectedTasks.has(task.taskId)}
                    onToggle={() => {
                      const newSet = new Set(selectedTasks);
                      if (newSet.has(task.taskId)) {
                        newSet.delete(task.taskId);
                      } else {
                        newSet.add(task.taskId);
                      }
                      setSelectedTasks(newSet);
                    }}
                    getScoreColor={getScoreColor}
                    getConfidenceBadge={getConfidenceBadge}
                  />
                ))}
              </div>
            </section>

            {/* Vakcompetenties */}
            <section className="mb-6">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                Vakcompetenties
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                  {selectedVakComps.size}/{result.vakCompetencySuggestions.length}
                </span>
              </h4>

              <div className="space-y-3">
                {result.vakCompetencySuggestions.map((comp) => (
                  <SuggestionCard
                    key={comp.competencyId}
                    name={comp.competencyName}
                    score={comp.suggestedScore}
                    confidence={comp.confidence}
                    toelichting={comp.toelichting}
                    reasoning={comp.reasoning}
                    sourceQuotes={comp.sourceQuotes}
                    requiresAgreement={comp.requiresAgreement}
                    agreement={comp.suggestedAgreement}
                    selected={selectedVakComps.has(comp.competencyId)}
                    onToggle={() => {
                      const newSet = new Set(selectedVakComps);
                      if (newSet.has(comp.competencyId)) {
                        newSet.delete(comp.competencyId);
                      } else {
                        newSet.add(comp.competencyId);
                      }
                      setSelectedVakComps(newSet);
                    }}
                    getScoreColor={getScoreColor}
                    getConfidenceBadge={getConfidenceBadge}
                  />
                ))}
              </div>
            </section>

            {/* Basiscompetenties */}
            <section>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                Basiscompetenties
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                  {selectedBasisComps.size}/{result.basisCompetencySuggestions.length}
                </span>
              </h4>

              <div className="space-y-3">
                {result.basisCompetencySuggestions.map((comp) => (
                  <SuggestionCard
                    key={comp.competencyId}
                    name={comp.competencyName}
                    score={comp.suggestedScore}
                    confidence={comp.confidence}
                    toelichting={comp.toelichting}
                    reasoning={comp.reasoning}
                    sourceQuotes={comp.sourceQuotes}
                    requiresAgreement={comp.requiresAgreement}
                    agreement={comp.suggestedAgreement}
                    selected={selectedBasisComps.has(comp.competencyId)}
                    onToggle={() => {
                      const newSet = new Set(selectedBasisComps);
                      if (newSet.has(comp.competencyId)) {
                        newSet.delete(comp.competencyId);
                      } else {
                        newSet.add(comp.competencyId);
                      }
                      setSelectedBasisComps(newSet);
                    }}
                    getScoreColor={getScoreColor}
                    getConfidenceBadge={getConfidenceBadge}
                  />
                ))}
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuleren
            </button>
            <button
              onClick={handleApply}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Check className="mr-2 h-4 w-4" />
              {totalSelected} suggesties toepassen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Subcomponent for individual suggestion
interface SuggestionCardProps {
  name: string;
  score: number | null;
  confidence: number;
  toelichting: string;
  reasoning: string;
  sourceQuotes: string[];
  requiresAgreement: boolean;
  agreement: { typeId: number; typeName: string; beschrijving: string } | null;
  selected: boolean;
  onToggle: () => void;
  getScoreColor: (score: number | null) => string;
  getConfidenceBadge: (confidence: number) => React.ReactNode;
}

function SuggestionCard({
  name,
  score,
  confidence,
  toelichting,
  reasoning,
  sourceQuotes,
  requiresAgreement,
  agreement,
  selected,
  onToggle,
  getScoreColor,
  getConfidenceBadge,
}: SuggestionCardProps) {
  const [showDetails, setShowDetails] = useState(false);

  if (score === null) {
    return (
      <div className="border rounded-md p-3 bg-gray-50 opacity-60">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            disabled
            checked={false}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{name}</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                Geen suggestie
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Onvoldoende informatie in de input</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`border rounded-md p-3 ${selected ? 'border-purple-300 bg-purple-50' : 'bg-white'}`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium">{name}</span>
            <span className={`text-2xl font-bold ${getScoreColor(score)}`}>{score}</span>
            {getConfidenceBadge(confidence)}
          </div>

          <p className="text-sm mb-2">{toelichting}</p>

          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            {showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {showDetails ? 'Verberg onderbouwing' : 'Toon onderbouwing'}
          </button>

          {showDetails && (
            <div className="mt-2 pl-3 border-l-2 border-gray-200 text-xs text-gray-600">
              <p className="mb-1">
                <strong>Redenering:</strong> {reasoning}
              </p>
              {sourceQuotes.length > 0 && (
                <p>
                  <strong>Citaten:</strong>{' '}
                  {sourceQuotes.map((q, i) => (
                    <span key={i} className="italic">
                      "{q}"{i < sourceQuotes.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </p>
              )}
            </div>
          )}

          {requiresAgreement && agreement && (
            <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded">
              <div className="flex items-center gap-1 text-orange-700 text-sm font-medium mb-1">
                <AlertTriangle className="h-4 w-4" />
                Afspraak vereist (score ≤ 2)
              </div>
              <p className="text-sm">
                <strong>Type:</strong> {agreement.typeName}
              </p>
              <p className="text-sm">
                <strong>Voorstel:</strong> {agreement.beschrijving}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
